<template>
  <Teleport to="body">
    <div v-if="visible" class="session-overlay">
      <div class="session-overlay__backdrop" />
      <div
        class="session-overlay__dialog"
        role="alertdialog"
        aria-modal="true"
        :aria-label="t('session.expired_title')"
      >
        <h2 class="session-overlay__title">{{ t('session.expired_title') }}</h2>
        <p class="session-overlay__message">{{ t('session.expired_message') }}</p>
        <button class="session-overlay__button" @click="handleLogin">
          {{ t('session.login_again') }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { clearAuthScopedCaches } from '@/composables/useAuthScopedCaches'

const { t } = useI18n()
const router = useRouter()
const visible = ref(false)

function onSessionExpired() {
  visible.value = true
}

async function handleLogin() {
  sessionStorage.setItem('redirect-after-login', window.location.pathname)
  visible.value = false
  // Drop per-user PWA cache bytes before sending the partner to
  // /login — the next session must never inherit the previous
  // user's `/api/v1/dashboard/*` or `/api/v1/mock/*` payloads.
  await clearAuthScopedCaches()
  router.push({ name: 'login' })
}

onMounted(() => {
  window.addEventListener('session:expired', onSessionExpired)
})

onUnmounted(() => {
  window.removeEventListener('session:expired', onSessionExpired)
})
</script>

<style scoped lang="scss">
.session-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.session-overlay__backdrop {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.session-overlay__dialog {
  position: relative;
  z-index: 1;
  background-color: var(--cds-layer);
  border-radius: 4px;
  padding: 2rem;
  max-width: 420px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}

.session-overlay__title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--cds-text-primary);
  margin-bottom: 0.75rem;
}

.session-overlay__message {
  font-size: 0.875rem;
  color: var(--cds-text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.session-overlay__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2.5rem;
  padding: 0 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  background-color: var(--app-brand-color, #0f62fe);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: var(--app-brand-color-hover, #0353e9);
  }

  &:focus-visible {
    outline: 2px solid var(--cds-focus);
    outline-offset: 2px;
  }
}
</style>
